import { Buffer } from "buffer";

import {
  PERSISTED_SESSION_CIPHER_ALGORITHM,
  PERSISTED_SESSION_IV_LENGTH_BYTES,
  PERSISTED_SESSION_KEY_LENGTH_BITS,
} from "./persisted-session.constants";
import { IPersistedSessionSecret } from "./persisted-session.models";

export const bytesToBase64 = (bytes: Uint8Array): string =>
  Buffer.from(bytes).toString("base64");

export const base64ToBuffer = (base64: string): Buffer =>
  Buffer.from(base64, "base64");

/** The ciphertext plus the IV it was encrypted with, both base64. */
export interface IEncryptedPayload {
  ivBase64: string;
  ciphertextBase64: string;
}

export const generateSessionKey = (): Promise<CryptoKey> =>
  crypto.subtle.generateKey(
    {
      name: PERSISTED_SESSION_CIPHER_ALGORITHM,
      length: PERSISTED_SESSION_KEY_LENGTH_BITS,
    },
    true,
    ["encrypt", "decrypt"],
  );

export const exportSessionKey = async (key: CryptoKey): Promise<string> =>
  bytesToBase64(new Uint8Array(await crypto.subtle.exportKey("raw", key)));

export const importSessionKey = (base64: string): Promise<CryptoKey> =>
  crypto.subtle.importKey(
    "raw",
    base64ToBuffer(base64),
    PERSISTED_SESSION_CIPHER_ALGORITHM,
    false,
    ["decrypt"],
  );

/** A fresh IV every call — required for AES-GCM, never reused across encryptions. */
export const encryptSecret = async (
  key: CryptoKey,
  secret: IPersistedSessionSecret,
): Promise<IEncryptedPayload> => {
  const iv = crypto.getRandomValues(
    new Uint8Array(PERSISTED_SESSION_IV_LENGTH_BYTES),
  );
  const plaintext = new TextEncoder().encode(JSON.stringify(secret));

  const ciphertext = await crypto.subtle.encrypt(
    { name: PERSISTED_SESSION_CIPHER_ALGORITHM, iv },
    key,
    plaintext,
  );

  return {
    ivBase64: bytesToBase64(iv),
    ciphertextBase64: bytesToBase64(new Uint8Array(ciphertext)),
  };
};

export const decryptSecret = async (
  key: CryptoKey,
  { ivBase64, ciphertextBase64 }: IEncryptedPayload,
): Promise<IPersistedSessionSecret> => {
  const plaintext = await crypto.subtle.decrypt(
    { name: PERSISTED_SESSION_CIPHER_ALGORITHM, iv: base64ToBuffer(ivBase64) },
    key,
    base64ToBuffer(ciphertextBase64),
  );

  return JSON.parse(new TextDecoder().decode(plaintext)) as IPersistedSessionSecret;
};
